import {
  OnboardingResultsAreIn,
  OnboardingCostFactors,
  OnboardingCarePhasesSummary,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
} from '@/pages';
import { PageLink } from '@/components';
import { Badge } from '@client/components/ui/badge';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { Check, Minus } from 'lucide-react';
import { TbProgressCheck } from 'react-icons/tb';

import { useSelector } from 'react-redux';

import { cn } from '@client/lib';
import { selectOnboardingSlideTracker } from '@client/model/selectors';
import { getFirstCarePhasePage } from '@/util/navigationLogic';
import { onboardingSlideByIndex } from '@/components/OnboardingSlideInfo';

interface ProgressStepperProps {
  client: Client;
  slideIndex: number;
  show: boolean;
}

export type StepDef = {
  name: 'Review Results' | 'Personalize Plan' | 'Finance Costs';
  beginningSlideIndex: number;
  lastSlideIndex: number;
};

export function getStepDefs(client: Client): StepDef[] {
  const clientFirstCarePhasePage = getFirstCarePhasePage(client);

  return [
    {
      name: 'Review Results',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(OnboardingResultsAreIn),
      lastSlideIndex: onboardingSlideByIndex.indexOf(OnboardingCostFactors),
    },
    {
      name: 'Personalize Plan',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(clientFirstCarePhasePage),
      lastSlideIndex: onboardingSlideByIndex.indexOf(OnboardingCarePhasesSummary),
    },
    {
      name: 'Finance Costs',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(OnboardingFundingSources),
      lastSlideIndex: onboardingSlideByIndex.indexOf(OnboardingFundingCoverage),
    },
  ];
}

export const ProgressStepper: React.FC<ProgressStepperProps> = ({ client, slideIndex, show }) => {
  const { slideHistory } = useSelector(selectOnboardingSlideTracker);

  if (!show) return null;

  const stepDefs = getStepDefs(client);
  const currentStep = findCurrentStep(stepDefs, slideIndex);

  const hasVisitedStep = (step: Step): boolean => {
    return slideHistory.some(
      visitedIndex =>
        visitedIndex >= step.beginningSlideIndex && visitedIndex <= step.lastSlideIndex,
    );
  };

  const renderStep = (step: Step, index: number) => {
    const stepPercentComplete = calcStepPercentComplete(slideIndex, step);
    const isCurrentStep = step === currentStep;
    const isOptionalStep = step.name === 'Personalize Plan';
    const isPastStep = slideIndex > step.lastSlideIndex;
    const wasVisited = hasVisitedStep(step);
    const isCompleted = isPastStep && wasVisited && !isCurrentStep;
    const isSkipped = isPastStep && !wasVisited && isOptionalStep;

    const stepContent = (
      <>
        <ProgressBar
          percent={isCompleted || isSkipped ? 100 : stepPercentComplete}
          isCurrentStep={isCurrentStep}
          isSkipped={isSkipped}
        />
        <div
          className={`flex items-center gap-2 text-xs ${isCurrentStep ? 'text-primary hover:text-primary-hover font-semibold' : 'font-medium text-gray-500'} md:text-sm`}
        >
          {isCompleted && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-50 p-1 font-semibold text-green-600">
              <Check
                className="h-4 w-4"
                strokeWidth={4}
              />
            </div>
          )}
          {isSkipped && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 p-1 font-semibold text-gray-500">
              <Minus
                className="h-4 w-4"
                strokeWidth={4}
              />
            </div>
          )}
          {isCurrentStep && (
            <TbProgressCheck className="text-primary hidden size-[18px] self-center md:flex" />
          )}
          <span>{step.name}</span>
          {isOptionalStep && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant="secondary"
                    className="px-1.5 py-0 text-[10px]"
                  >
                    Optional
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs p-2 text-xs font-normal text-gray-700"
                >
                  These estimates are based on default assumptions. You can optionally customize
                  them to better reflect {client.clientFirstName}&apos;s specific situation.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </>
    );

    return (
      <li
        key={step.name}
        className="flex-1"
      >
        <PageLink
          to={onboardingSlideByIndex[step.beginningSlideIndex]}
          targetProps={{ clientId: client.clientId }}
          className="group flex flex-col"
        >
          {stepContent}
        </PageLink>
      </li>
    );
  };

  return (
    <nav
      aria-label="Progress"
      className="mx-auto w-full max-w-4xl px-2 pb-2 md:px-0"
    >
      <ol className="flex space-x-2 space-y-0 md:space-x-8">{stepDefs.map(renderStep)}</ol>
    </nav>
  );
};

interface ProgressBarProps {
  percent: number; // Percent of the progress bar to fill
  isCurrentStep?: boolean;
  isSkipped?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ percent, isCurrentStep, isSkipped }) => {
  return (
    <div
      className={cn('mb-2 h-1 w-full rounded-md bg-gray-200', {
        'bg-primary/30': isCurrentStep,
      })}
    >
      <div
        className={cn('h-1 rounded-md', {
          'bg-primary': !isSkipped,
          'bg-gray-400': isSkipped,
        })}
        style={{ width: `${percent}%` }}
      ></div>
    </div>
  );
};

function findCurrentStep(steps: Step[], slideNumber: number): Step | undefined {
  return steps.find(
    step => slideNumber >= step.beginningSlideIndex && slideNumber <= step.lastSlideIndex,
  );
}

interface Step {
  name: string;
  beginningSlideIndex: number;
  lastSlideIndex: number;
}

function calcStepPercentComplete(slideIndex: number, step: Step): number {
  if (slideIndex < step.beginningSlideIndex) return 0;
  if (slideIndex > step.lastSlideIndex) return 100;

  return Math.round(
    ((slideIndex - step.beginningSlideIndex) /
      (step.lastSlideIndex - step.beginningSlideIndex + 1)) *
      100,
  );
}
